/**
 * templates/metadata.js - Extended metadata definitions for templates
 * Location: /app/frontend/src/admin/page_builder/templates/
 * 
 * Provides additional metadata for the Wix-style Add Elements panel:
 * - thumbnail: Preview image path
 * - subCategory: Finer categorization within categories
 * - tags: Searchable keywords
 * - isDataBacked: Whether the section fetches live data from API
 * - isNew: Flag for recently added templates
 * - popularity: Usage ranking for "Most Used" filter
 */

/**
 * Sub-category definitions
 * These provide finer categorization within main categories
 */
export const SubCategory = {
  // Hero sub-categories
  HERO_LANDING: 'landing',
  HERO_PAGE: 'page',
  HERO_CAMPAIGN: 'campaign',
  
  // Content sub-categories
  CONTENT_RAILS: 'rails',
  CONTENT_GRIDS: 'grids',
  CONTENT_LISTS: 'lists',
  CONTENT_PERSONALIZED: 'personalized',
  
  // Reader sub-categories
  READER_NAVIGATION: 'navigation',
  READER_PRESENTATION: 'presentation',
  
  // Creator sub-categories
  CREATOR_SHOWCASE: 'showcase',
  CREATOR_RANKINGS: 'rankings',
  CREATOR_CTA: 'cta',
  
  // CTA sub-categories
  CTA_BANNERS: 'banners',
  CTA_ANNOUNCEMENTS: 'announcements',
  CTA_FOOTERS: 'footers',
  
  // Gallery sub-categories
  GALLERY_ARTWORK: 'artwork',
  GALLERY_PORTFOLIO: 'portfolio',
  
  // Series sub-categories
  SERIES_HERO: 'series_hero',
  SERIES_CONTENT: 'series_content',
  SERIES_ENGAGEMENT: 'series_engagement',
  
  // Admin sub-categories
  ADMIN_SYSTEM: 'system',
};

/**
 * Sub-category metadata
 */
export const SubCategoryMeta = {
  // Hero
  [SubCategory.HERO_LANDING]: {
    label: 'Landing Heroes',
    description: 'Full-width impact sections for landing pages',
  },
  [SubCategory.HERO_PAGE]: {
    label: 'Page Headers',
    description: 'Clean headers for content pages',
  },
  [SubCategory.HERO_CAMPAIGN]: {
    label: 'Campaign Heroes',
    description: 'Promotional headers with CTAs',
  },
  
  // Content
  [SubCategory.CONTENT_RAILS]: {
    label: 'Content Rails',
    description: 'Horizontal scrolling content displays',
  },
  [SubCategory.CONTENT_GRIDS]: {
    label: 'Content Grids',
    description: 'Multi-column content layouts',
  },
  [SubCategory.CONTENT_LISTS]: {
    label: 'Content Lists',
    description: 'Vertical content listings',
  },
  [SubCategory.CONTENT_PERSONALIZED]: {
    label: 'Personalized',
    description: 'User-specific content sections',
  },
  
  // Reader
  [SubCategory.READER_NAVIGATION]: {
    label: 'Navigation',
    description: 'Chapter lists and navigation elements',
  },
  [SubCategory.READER_PRESENTATION]: {
    label: 'Presentation',
    description: 'Content reading experiences',
  },
  
  // Creator
  [SubCategory.CREATOR_SHOWCASE]: {
    label: 'Creator Showcases',
    description: 'Feature and spotlight creators',
  },
  [SubCategory.CREATOR_RANKINGS]: {
    label: 'Rankings & Lists',
    description: 'Leaderboards and creator lists',
  },
  [SubCategory.CREATOR_CTA]: {
    label: 'Creator CTAs',
    description: 'Call-to-action for creators',
  },
  
  // CTA
  [SubCategory.CTA_BANNERS]: {
    label: 'Banners',
    description: 'Promotional banner sections',
  },
  [SubCategory.CTA_ANNOUNCEMENTS]: {
    label: 'Announcements',
    description: 'News and update banners',
  },
  [SubCategory.CTA_FOOTERS]: {
    label: 'Footer Sections',
    description: 'Page footer elements',
  },
  
  // Gallery
  [SubCategory.GALLERY_ARTWORK]: {
    label: 'Artwork Display',
    description: 'Art gallery and showcase grids',
  },
  [SubCategory.GALLERY_PORTFOLIO]: {
    label: 'Portfolio',
    description: 'Creator portfolio displays',
  },
  
  // Series
  [SubCategory.SERIES_HERO]: {
    label: 'Series Headers',
    description: 'Hero sections for series detail pages',
  },
  [SubCategory.SERIES_CONTENT]: {
    label: 'Series Content',
    description: 'Episode lists and info panels',
  },
  [SubCategory.SERIES_ENGAGEMENT]: {
    label: 'Engagement',
    description: 'Comments, support CTAs, related content',
  },
  
  // Admin
  [SubCategory.ADMIN_SYSTEM]: {
    label: 'System Sections',
    description: 'Admin-only system components',
  },
};

/**
 * Filter types for the middle pane
 */
export const FilterType = {
  RECOMMENDED: 'recommended',
  MOST_USED: 'most_used',
  NEW: 'new',
  DATA_BACKED: 'data_backed',
  STATIC: 'static',
};

/**
 * Filter metadata
 */
export const FilterMeta = {
  [FilterType.RECOMMENDED]: {
    label: 'Recommended',
    description: 'Best for your page type',
    icon: 'sparkles',
  },
  [FilterType.MOST_USED]: {
    label: 'Most Used',
    description: 'Popular templates',
    icon: 'trending-up',
  },
  [FilterType.NEW]: {
    label: 'New',
    description: 'Recently added templates',
    icon: 'plus-circle',
  },
  [FilterType.DATA_BACKED]: {
    label: 'Data-backed',
    description: 'Fetch live content from API',
    icon: 'database',
  },
  [FilterType.STATIC]: {
    label: 'Static',
    description: 'Manually configured content',
    icon: 'file-text',
  },
};

/**
 * Extended template metadata
 * Maps template IDs to additional metadata not in the base template definition
 */
export const TEMPLATE_METADATA = {
  // Hero templates
  hero_overlay: {
    subCategory: SubCategory.HERO_LANDING,
    tags: ['hero', 'landing', 'full-width', 'background', 'cta', 'campaign'],
    isDataBacked: false,
    isNew: false,
    popularity: 95,
    thumbnail: '/admin/templates/hero-overlay.svg',
  },
  hero_split: {
    subCategory: SubCategory.HERO_CAMPAIGN,
    tags: ['hero', 'split', 'media', 'product', 'feature', '50-50'],
    isDataBacked: false,
    isNew: false,
    popularity: 75,
    thumbnail: '/admin/templates/hero-split.svg',
  },
  hero_minimal: {
    subCategory: SubCategory.HERO_PAGE,
    tags: ['hero', 'minimal', 'clean', 'text', 'header', 'simple'],
    isDataBacked: false,
    isNew: false,
    popularity: 80,
    thumbnail: '/admin/templates/hero-minimal.svg',
  },
  
  // Content templates
  trending_rail: {
    subCategory: SubCategory.CONTENT_RAILS,
    tags: ['trending', 'rail', 'popular', 'hot', 'carousel', 'horizontal'],
    isDataBacked: true,
    isNew: false,
    popularity: 100,
    thumbnail: '/admin/templates/trending-rail.svg',
  },
  featured_grid: {
    subCategory: SubCategory.CONTENT_GRIDS,
    tags: ['featured', 'grid', 'editorial', 'picks', 'curated', 'columns'],
    isDataBacked: true,
    isNew: false,
    popularity: 90,
    thumbnail: '/admin/templates/featured-grid.svg',
  },
  new_releases_rail: {
    subCategory: SubCategory.CONTENT_RAILS,
    tags: ['new', 'releases', 'latest', 'recent', 'fresh', 'rail'],
    isDataBacked: true,
    isNew: false,
    popularity: 88,
    thumbnail: '/admin/templates/new-releases.svg',
  },
  continue_reading: {
    subCategory: SubCategory.CONTENT_PERSONALIZED,
    tags: ['continue', 'reading', 'progress', 'personalized', 'user', 'history'],
    isDataBacked: true,
    isNew: false,
    popularity: 70,
    thumbnail: '/admin/templates/continue-reading.svg',
  },
  genre_spotlight: {
    subCategory: SubCategory.CONTENT_GRIDS,
    tags: ['genre', 'spotlight', 'category', 'collection', 'curated'],
    isDataBacked: true,
    isNew: false,
    popularity: 65,
    thumbnail: '/admin/templates/genre-spotlight.svg',
  },
  community_picks: {
    subCategory: SubCategory.CONTENT_GRIDS,
    tags: ['community', 'picks', 'curated', 'favorites', 'staff', 'editorial'],
    isDataBacked: true,
    isNew: false,
    popularity: 60,
    thumbnail: '/admin/templates/community-picks.svg',
  },
  
  // Reader templates
  chapter_list: {
    subCategory: SubCategory.READER_NAVIGATION,
    tags: ['chapter', 'list', 'navigation', 'toc', 'contents', 'episodes'],
    isDataBacked: true,
    isNew: false,
    popularity: 85,
    thumbnail: '/admin/templates/chapter-list.svg',
  },
  vertical_reader_stack: {
    subCategory: SubCategory.READER_PRESENTATION,
    tags: ['reader', 'vertical', 'webtoon', 'scroll', 'comic', 'stack'],
    isDataBacked: true,
    isNew: false,
    popularity: 80,
    thumbnail: '/admin/templates/vertical-reader.svg',
  },
  horizontal_reader: {
    subCategory: SubCategory.READER_PRESENTATION,
    tags: ['reader', 'horizontal', 'manga', 'swipe', 'page', 'turn'],
    isDataBacked: true,
    isNew: false,
    popularity: 70,
    thumbnail: '/admin/templates/horizontal-reader.svg',
  },
  
  // Creator templates
  creator_spotlight: {
    subCategory: SubCategory.CREATOR_SHOWCASE,
    tags: ['creator', 'spotlight', 'featured', 'profile', 'artist', 'author'],
    isDataBacked: true,
    isNew: false,
    popularity: 75,
    thumbnail: '/admin/templates/creator-spotlight.svg',
  },
  creator_grid: {
    subCategory: SubCategory.CREATOR_SHOWCASE,
    tags: ['creator', 'grid', 'team', 'artists', 'authors', 'multi'],
    isDataBacked: true,
    isNew: false,
    popularity: 65,
    thumbnail: '/admin/templates/creator-grid.svg',
  },
  creator_cta: {
    subCategory: SubCategory.CREATOR_CTA,
    tags: ['creator', 'cta', 'join', 'signup', 'publish', 'call-to-action'],
    isDataBacked: false,
    isNew: false,
    popularity: 55,
    thumbnail: '/admin/templates/creator-cta.svg',
  },
  leaderboard_strip: {
    subCategory: SubCategory.CREATOR_RANKINGS,
    tags: ['leaderboard', 'rankings', 'top', 'creators', 'stats', 'strip'],
    isDataBacked: true,
    isNew: false,
    popularity: 60,
    thumbnail: '/admin/templates/leaderboard.svg',
  },
  
  // CTA templates
  cta_banner: {
    subCategory: SubCategory.CTA_BANNERS,
    tags: ['cta', 'banner', 'promotion', 'action', 'signup', 'newsletter'],
    isDataBacked: false,
    isNew: false,
    popularity: 70,
    thumbnail: '/admin/templates/cta-banner.svg',
  },
  transparency_footer: {
    subCategory: SubCategory.CTA_FOOTERS,
    tags: ['footer', 'transparency', 'trust', 'info', 'values', 'about'],
    isDataBacked: false,
    isNew: false,
    popularity: 50,
    thumbnail: '/admin/templates/transparency-footer.svg',
  },
  announcement_banner: {
    subCategory: SubCategory.CTA_ANNOUNCEMENTS,
    tags: ['announcement', 'banner', 'news', 'alert', 'update', 'notice'],
    isDataBacked: false,
    isNew: true,
    popularity: 45,
    thumbnail: '/admin/templates/announcement.svg',
  },
  category_spotlight: {
    subCategory: SubCategory.CTA_BANNERS,
    tags: ['category', 'spotlight', 'promotion', 'feature', 'highlight'],
    isDataBacked: false,
    isNew: false,
    popularity: 40,
    thumbnail: '/admin/templates/category-spotlight.svg',
  },
  
  // Gallery templates
  art_gallery_grid: {
    subCategory: SubCategory.GALLERY_ARTWORK,
    tags: ['gallery', 'art', 'artwork', 'masonry', 'grid', 'portfolio'],
    isDataBacked: true,
    isNew: false,
    popularity: 72,
    thumbnail: '/admin/templates/art-gallery.svg',
  },
  
  // Series Detail templates
  series_hero: {
    subCategory: SubCategory.SERIES_HERO,
    tags: ['series', 'hero', 'cover', 'title', 'banner', 'comic', 'novel'],
    isDataBacked: true,
    isNew: true,
    popularity: 90,
    thumbnail: '/admin/templates/series-hero.svg',
  },
  episode_list: {
    subCategory: SubCategory.SERIES_CONTENT,
    tags: ['episodes', 'chapters', 'list', 'navigation', 'series', 'toc'],
    isDataBacked: true,
    isNew: true,
    popularity: 95,
    thumbnail: '/admin/templates/episode-list.svg',
  },
  series_info_panel: {
    subCategory: SubCategory.SERIES_CONTENT,
    tags: ['series', 'info', 'description', 'tags', 'stats', 'metadata'],
    isDataBacked: true,
    isNew: true,
    popularity: 70,
    thumbnail: '/admin/templates/series-info.svg',
  },
  series_comments: {
    subCategory: SubCategory.SERIES_ENGAGEMENT,
    tags: ['comments', 'discussion', 'reactions', 'feedback', 'community'],
    isDataBacked: true,
    isNew: true,
    popularity: 65,
    thumbnail: '/admin/templates/series-comments.svg',
  },
  related_series: {
    subCategory: SubCategory.SERIES_ENGAGEMENT,
    tags: ['related', 'similar', 'recommendations', 'more', 'discover'],
    isDataBacked: true,
    isNew: true,
    popularity: 75,
    thumbnail: '/admin/templates/related-series.svg',
  },
  series_creator_spotlight: {
    subCategory: SubCategory.SERIES_CONTENT,
    tags: ['creator', 'author', 'artist', 'profile', 'follow', 'series'],
    isDataBacked: true,
    isNew: true,
    popularity: 60,
    thumbnail: '/admin/templates/series-creator.svg',
  },
  series_cta: {
    subCategory: SubCategory.SERIES_ENGAGEMENT,
    tags: ['support', 'tip', 'donate', 'share', 'premium', 'cta'],
    isDataBacked: false,
    isNew: true,
    popularity: 55,
    thumbnail: '/admin/templates/series-cta.svg',
  },
  
  // Admin templates
  admin_pages_list: {
    subCategory: SubCategory.ADMIN_SYSTEM,
    tags: ['admin', 'pages', 'registry', 'system', 'management'],
    isDataBacked: true,
    isNew: false,
    popularity: 30,
    thumbnail: '/admin/templates/admin-pages.svg',
  },
};

/**
 * Get extended metadata for a template
 * @param {string} templateId - Template ID
 * @returns {Object} Extended metadata with defaults
 */
export function getTemplateMetadata(templateId) {
  const meta = TEMPLATE_METADATA[templateId] || {};
  return {
    subCategory: meta.subCategory || null,
    tags: meta.tags || [],
    isDataBacked: meta.isDataBacked ?? false,
    isNew: meta.isNew ?? false,
    popularity: meta.popularity ?? 50,
    thumbnail: meta.thumbnail || null,
  };
}

/**
 * Get all sub-categories for a category
 * @param {string} category - Main category ID
 * @returns {Array} Sub-categories with metadata
 */
export function getSubCategoriesForCategory(category) {
  const prefixMap = {
    hero: 'HERO_',
    content: 'CONTENT_',
    reader: 'READER_',
    creator: 'CREATOR_',
    cta: 'CTA_',
    gallery: 'GALLERY_',
    series: 'SERIES_',
    admin: 'ADMIN_',
  };
  
  const prefix = prefixMap[category];
  if (!prefix) return [];
  
  return Object.entries(SubCategory)
    .filter(([key]) => key.startsWith(prefix))
    .map(([key, value]) => ({
      id: value,
      ...SubCategoryMeta[value],
    }));
}

/**
 * Filter templates by filter type
 * @param {Array} templates - Array of templates with merged metadata
 * @param {string} filterType - Filter type from FilterType enum
 * @returns {Array} Filtered templates
 */
export function filterTemplatesByType(templates, filterType) {
  switch (filterType) {
    case FilterType.RECOMMENDED:
      // Top 50% by popularity
      const sorted = [...templates].sort((a, b) => b.popularity - a.popularity);
      return sorted.slice(0, Math.ceil(sorted.length / 2));
    
    case FilterType.MOST_USED:
      return [...templates].sort((a, b) => b.popularity - a.popularity);
    
    case FilterType.NEW:
      return templates.filter(t => t.isNew);
    
    case FilterType.DATA_BACKED:
      return templates.filter(t => t.isDataBacked);
    
    case FilterType.STATIC:
      return templates.filter(t => !t.isDataBacked);
    
    default:
      return templates;
  }
}

export default {
  SubCategory,
  SubCategoryMeta,
  FilterType,
  FilterMeta,
  TEMPLATE_METADATA,
  getTemplateMetadata,
  getSubCategoriesForCategory,
  filterTemplatesByType,
};
